import { useMemo, useState, useId, memo } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Inbox, Reply } from 'lucide-react';
import {
  groupInbox,
  selectInbox,
  type InboxEntry,
  type InboxFilter,
  type InboxSort,
  type InboxThread,
} from '@/lib/mentionsInbox';
import { useResponseClock } from '@/lib/responseClock';
import { messageTime, type MessageActions } from '@/lib/conversation';
import { Avatar } from './Avatar';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { EmptyState } from './EmptyState';
import { EntityText } from './EntityText';
import { ConversationFeed, Message } from './Message';
import { MessageContent } from './MessageContent';
import { ResponseTimer } from './ResponseTimer';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
import { Skeleton } from './Skeleton';
import { cn } from './cn';

export type InboxDensity = 'comfortable' | 'compact';

export interface InboxAgeOption {
  value: number;
  label: string;
}
interface MentionsInboxProps extends MessageActions {
  entries: readonly InboxEntry[];
  filter: InboxFilter;
  onFilterChange: (filter: InboxFilter) => void;
  density?: InboxDensity;
  onDensityChange?: (density: InboxDensity) => void;
  sort: InboxSort;
  onSortChange: (sort: InboxSort) => void;
  /** Age choices are supplied by the owner, as are response thresholds. */
  ageOptions?: readonly InboxAgeOption[];
  state?: 'ready' | 'loading' | 'error';
  onRetry?: () => void;
  onOpenSource?: (thread: InboxThread, trigger: HTMLButtonElement) => void;
  onReply?: (thread: InboxThread, trigger: HTMLButtonElement) => void;
}
export function MentionsInbox({
  entries,
  filter,
  onFilterChange,
  sort,
  onSortChange,
  ageOptions = [],
  density = 'comfortable',
  onDensityChange,
  state = 'ready',
  onRetry,
  onOpenSource,
  onReply,
  responseNow,
  entities,
  people,
  onEntityOpen,
  onAttachmentOpen,
  onReaction,
  timeZone,
}: MentionsInboxProps) {
  const actions = useMemo(
    () => ({ entities, people, onEntityOpen, onAttachmentOpen, onReaction, timeZone }),
    [entities, people, onEntityOpen, onAttachmentOpen, onReaction, timeZone],
  );
  // Group identity is data-owned. Ticks do not recreate the rows or move focus.
  const groups = useMemo(() => groupInbox(entries), [entries]);
  const clock = useResponseClock(responseNow);
  const selected = useMemo(() => selectInbox(groups, filter, sort, clock), [groups, filter, sort, clock]);
  const filtered = filter.mine || filter.unread || filter.waiting || filter.minAgeMs > 0;
  return (
    <section data-slot="mentions-inbox" aria-label="Единый ящик упоминаний" className="min-w-0">
      <div className="mb-4 flex flex-wrap items-center gap-4 rounded-md border border-line bg-muted p-3">
        <span className="type-overline text-fg-muted">Показать</span>
        <Checkbox
          label="Мои"
          checked={filter.mine}
          onCheckedChange={(checked) => onFilterChange({ ...filter, mine: checked === true })}
        />
        <Checkbox
          label="Непрочитанные"
          checked={filter.unread}
          onCheckedChange={(checked) => onFilterChange({ ...filter, unread: checked === true })}
        />
        <Checkbox
          label="Ждут ответа"
          checked={filter.waiting}
          onCheckedChange={(checked) => onFilterChange({ ...filter, waiting: checked === true })}
        />
        {ageOptions.length > 0 && (
          <div className="w-48">
            <Select
              value={String(filter.minAgeMs)}
              onValueChange={(value) => onFilterChange({ ...filter, minAgeMs: Number(value) })}
            >
              <SelectTrigger aria-label="Фильтр по сроку ответа">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Любой срок</SelectItem>
                {ageOptions
                  .filter((option) => option.value > 0)
                  .map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="ml-auto w-56">
          <Select value={sort} onValueChange={(value) => onSortChange(value as InboxSort)}>
            <SelectTrigger aria-label="Порядок обращений">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Сначала новые</SelectItem>
              <SelectItem value="responseAge">Дольше ждут ответа</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3 text-xs text-fg-muted">
        {onDensityChange && (
          <Checkbox
            label="Плотно"
            checked={density === 'compact'}
            onCheckedChange={(checked) => onDensityChange(checked === true ? 'compact' : 'comfortable')}
          />
        )}
        <span role="status">
          {state === 'loading' ? 'Загрузка обращений…' : 'Ветки: ' + selected.length + ' из ' + groups.length}
        </span>
        {filtered && (
          <Button
            variant="link"
            size="sm"
            onClick={() => onFilterChange({ mine: false, unread: false, waiting: false, minAgeMs: 0 })}
          >
            Сбросить фильтры
          </Button>
        )}
      </div>
      {state === 'error' && (
        <div
          role="alert"
          className="mb-4 flex flex-wrap items-center gap-3 rounded-md border border-danger-line bg-danger-soft p-3 text-sm text-danger-strong"
        >
          <p className="min-w-0 flex-1">Не удалось обновить обращения. Загруженные ветки сохранены.</p>
          {onRetry && (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              Повторить
            </Button>
          )}
        </div>
      )}
      {state === 'loading' ? (
        <div
          aria-busy="true"
          aria-label="Загрузка ящика"
          className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-card"
        >
          {[0, 1, 2].map((id) => (
            <InboxRowSkeleton
              key={id}
              density={density}
              waiting={selected[id] ? !!selected[id].response : true}
            />
          ))}
        </div>
      ) : selected.length ? (
        <ol
          aria-label="Ветки обращений"
          className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-card"
        >
          {selected.map((thread) => (
            <InboxRow
              key={thread.key}
              thread={thread}
              density={density}
              actions={actions}
              responseNow={responseNow}
              onOpenSource={onOpenSource}
              onReply={onReply}
            />
          ))}
        </ol>
      ) : (
        <EmptyState
          icon={<Inbox />}
          title={filtered ? 'Под эти фильтры обращений нет' : 'Новых обращений пока нет'}
          description={
            filtered
              ? 'Измени условия или сбрось фильтры, чтобы увидеть остальные ветки.'
              : 'Здесь появятся упоминания и обращения из подключённых каналов.'
          }
          action={
            filtered
              ? {
                  label: 'Сбросить фильтры',
                  onClick: () => onFilterChange({ mine: false, unread: false, waiting: false, minAgeMs: 0 }),
                }
              : undefined
          }
        />
      )}
    </section>
  );
}
const InboxRow = memo(function InboxRow({
  density,
  thread,
  actions,
  responseNow,
  onOpenSource,
  onReply,
}: {
  thread: InboxThread;
  density: InboxDensity;
  actions: MessageActions;
  responseNow?: number;
  onOpenSource?: MentionsInboxProps['onOpenSource'];
  onReply?: MentionsInboxProps['onReply'];
}) {
  const [expanded, setExpanded] = useState(false);
  const regionId = useId();
  const { message } = thread.latest;
  return (
    <li data-slot="inbox-row" data-thread-key={thread.key}>
      <article className={cn('group/inbox flex min-w-0 gap-3', density === 'compact' ? 'px-3 py-2' : 'p-4')}>
        <Avatar name={message.author.name} src={message.author.avatar} size="md" className="shrink-0" />
        <div className="min-w-0 flex-1">
          <header className="flex h-6 min-w-0 items-center gap-3">
            <h3 className="min-w-0 flex-1 truncate type-subheading" title={thread.subject}>
              {thread.subject}
            </h3>
            {thread.unreadCount > 0 && (
              <span className="shrink-0 text-xs font-medium text-brand-fg">
                {thread.unreadCount}{' '}
                {thread.unreadCount % 10 === 1 && thread.unreadCount % 100 !== 11 ? 'новое' : 'новых'}
                <span className="sr-only">, непрочитанные обращения</span>
              </span>
            )}
            <time
              dateTime={message.sentAt}
              title={messageTime(message.sentAt, actions.timeZone, true)}
              className="text-xs text-fg-muted tabular-nums"
            >
              {messageTime(message.sentAt, actions.timeZone)}
            </time>
          </header>
          <div className="flex h-4 min-w-0 items-center gap-2 text-xs text-fg-muted">
            {actions.onEntityOpen ? (
              <EntityText
                variant="author"
                person={actions.people?.find((person) => person.id === message.author.id)}
                name={message.author.name}
                onClick={(event) =>
                  actions.onEntityOpen?.({ ...message.author, kind: 'user' }, event.currentTarget)
                }
              />
            ) : (
              <span>{message.author.name}</span>
            )}
            <span className="truncate">
              {message.source?.label ?? 'Канал'} · {message.source?.channel ?? 'Обращения'}
            </span>
            {thread.mine && <span className="ml-auto shrink-0">Мне</span>}
          </div>
          <div className={cn('overflow-hidden', density === 'compact' ? 'mt-1 h-5' : 'mt-2 h-10')}>
            <MessageContent
              preview
              content={message.content}
              entities={actions.entities}
              onEntityOpen={actions.onEntityOpen}
            />
          </div>
          <footer
            className={cn(
              'flex min-h-8 flex-wrap items-center gap-2',
              density === 'compact' ? 'mt-1' : 'mt-3',
            )}
          >
            <button
              type="button"
              data-slot="inbox-thread-toggle"
              aria-expanded={expanded}
              aria-controls={regionId}
              onClick={() => setExpanded(!expanded)}
              className="inline-flex h-8 items-center gap-1 rounded-sm px-2 text-xs font-medium text-brand-fg hover:bg-hover focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              {expanded ? (
                <ChevronDown className="size-3.5" aria-hidden />
              ) : (
                <ChevronRight className="size-3.5" aria-hidden />
              )}
              {thread.entries.length} в ветке
            </button>
            {thread.response && (
              <ResponseTimer wait={thread.response} now={responseNow} timeZone={actions.timeZone} />
            )}
            <div
              data-slot="inbox-actions"
              className="pointer-events-none ml-auto flex items-center gap-2 opacity-0 transition-opacity duration-fast group-focus-within/inbox:pointer-events-auto group-focus-within/inbox:opacity-100 group-hover/inbox:pointer-events-auto group-hover/inbox:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100"
            >
              <Button
                variant="ghost"
                size="sm"
                disabled={!onOpenSource}
                onClick={(event) => onOpenSource?.(thread, event.currentTarget)}
                aria-label={'Открыть в источнике: ' + thread.subject}
              >
                <ExternalLink className="size-3.5" aria-hidden />В источнике
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!onReply}
                onClick={(event) => onReply?.(thread, event.currentTarget)}
                aria-label={'Ответить: ' + thread.subject}
              >
                <Reply className="size-3.5" aria-hidden />
                Ответить
              </Button>
            </div>
          </footer>
        </div>
      </article>
      {expanded && (
        <div
          id={regionId}
          role="region"
          aria-label={thread.subject}
          className="mx-4 mb-4 border-l border-line"
        >
          <ConversationFeed label={'Обращения: ' + thread.subject}>
            {thread.entries.map((entry) => (
              <li key={entry.id}>
                <Message message={entry.message} {...actions} responseNow={responseNow} />
              </li>
            ))}
          </ConversationFeed>
        </div>
      )}
    </li>
  );
});
/** Same 24/16/40/32px content rows, 12px footer gap and 16px outer padding as InboxRow. */
export function InboxRowSkeleton({
  still = false,
  waiting = true,
  density = 'comfortable',
}: {
  still?: boolean;
  waiting?: boolean;
  density?: InboxDensity;
}) {
  return (
    <div
      data-slot="inbox-row-skeleton"
      aria-hidden
      className={cn('flex gap-3', density === 'compact' ? 'px-3 py-2' : 'p-4')}
    >
      <Skeleton still={still} className="size-8 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1">
        <div className="flex h-6 items-center justify-between gap-3">
          <Skeleton still={still} className="w-48" />
          <Skeleton still={still} className="w-10" />
        </div>
        <div className="flex h-4 items-center gap-3">
          <Skeleton still={still} className="w-32" />
          <Skeleton still={still} className="w-48" />
        </div>
        <div className={cn('flex flex-col justify-around', density === 'compact' ? 'mt-1 h-5' : 'mt-2 h-10')}>
          <Skeleton still={still} className="w-full" />
          {density !== 'compact' && <Skeleton still={still} className="w-3/4" />}
        </div>
        <div
          className={cn('flex min-h-8 flex-wrap items-center gap-2', density === 'compact' ? 'mt-1' : 'mt-3')}
        >
          <div className="flex h-8 w-24 items-center">
            <Skeleton still={still} className="w-full" />
          </div>
          {waiting && (
            <div className="flex h-5 w-60 max-w-full items-center">
              <Skeleton still={still} className="w-40" />
            </div>
          )}
          <div className="ml-auto flex h-8 w-64 max-w-full items-center">
            <Skeleton still={still} className="w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
